<template>
  <div class="audiences row">
    <div class="flex justify-between items-center">
      <div class="flex flex-col">
        <h1 class="page-header" style="margin: 0">audiences</h1>
        <div class="text-lg leading-none">view or build a new audience</div>
        <div class="text-xs text-error" v-if="error">{{error.message}}</div>
      </div>
      <button
        class="
          h-12
          button
          block
          font-primary
          py-1
          px-5
          text-base text-white text-opacity-70
          rounded-md
        "
        @click="createNewAudience()"
      >
        build new audience
      </button>
    </div>
    <div
      v-if="
        tableRows.length <= 0 &&
        !loadingAudiences &&
        tableIncompleteRows.length <= 0 &&
        !loadingAudiencesIncomplete
      "
      class="w-full text-3xl text-center mt-24"
    >
      build your first audience
    </div>
    <div
      v-if="!audiences && loadingAudiences"
      class="w-full flex justify-center mt-24"
    >
      <loading-icon />
    </div>
    <div v-if="tableIncompleteRows.length > 0" class="mt-6">
      <div class="text-2xl text-center">building...</div>
      <custom-grid-table
        :rows="tableIncompleteRows"
        :columns="tableIncompleteColumns"
        disableTableSort
        :loading="loadingAudiencesIncomplete"
      >
        <template #view="{ row }">
          <span style="color:#4a4a4a">{{ row.state.replaceAll('_', ' ') }}</span>
        </template>
      </custom-grid-table>
    </div>
    <div v-if="tableRows.length > 0" class="mt-6">
      <custom-grid-table
        :rows="tableRows"
        :columns="tableColumns"
        @sortChange="updateTableSort"
        :defaultSortDirection="sortDirection"
        :defaultSortColumn="sortColumn"
        disableTableSort
        sortable
        :loading="loadingAudiences"
      >
        <template #view="{ row }">
          <a :href="`/audiences/${row.audience_id}`" class="no-underline text-blue"> view </a>
        </template>
      </custom-grid-table>
      <div v-if="pagesTotal > 1" class="flex justify-center items-center mt-10">
        <pagination :page.sync="page" :pages="pagesTotal" :gap="2" />
      </div>
    </div>
  </div>
</template>

<script>
import {
  CustomGridTable,
  Pagination,
  LoadingIcon,
} from "@springbig/sb-components";
import { audiencesAPI } from "../../services/api/audiencesAPI";
import { shortenNumber } from "../../utilities";
import dayjs from "dayjs";

export default {
  name: "Audiences",
  components: {
    CustomGridTable,
    LoadingIcon,
    Pagination,
  },
  data() {
    return {
      audiences: null,
      audiencesIncomplete: null,
      loadingAudiences: false,
      loadingAudiencesIncomplete: false,
      page: 0,
      sortColumn: "date_created",
      sortDirection: "desc",
      tableRows: [],
      tableColumns: [
        { key: "audience_name", value: "audience name" },
        { key: "date_created", value: "date created" },
        { key: "total_members", value: "total members" },
        { key: "total_spend", value: "total spend" },
        { key: "total_visits", value: "total visits" },
        { key: "avg_spend", value: "avg spend" },
        { key: "avg_ticket", value: "avg ticket" },
        { key: "msgs_sent", value: "msgs sent" },
      ],
      tableIncompleteRows: [],
      tableIncompleteColumns: [
        { key: "audience_name", value: "audience name" },
        { key: "state_desc", value: "state" },
      ],
      error: {},
      audienceStateName: {
        'queued': 'building',
        'in_progress': 'updating',
        'failed': 'failed',
        'completed': 'completed',
      }
    };
  },
  async created() {
    this.fetchAudiences();
    this.fetchAudiencesIncomplete();
  },
  watch: {
    page() {
      this.fetchAudiences();
    },
  },
  computed: {
    pagesTotal() {
      return this.audiences?.pagination_data?.total_pages || 1;
    },
  },
  methods: {
    updateTableSort(sort) {
      if (
        (this.sortColumn !== sort.key || this.sortDirection !== sort.sorting) &&
        this.tableRows.length > 0
      ) {
        this.page = 0;
        this.sortColumn = sort.key;
        this.sortDirection = sort.sorting;
        this.fetchAudiences();
      }
    },
    async fetchAudiencesIncomplete() {
      this.loadingAudiencesIncomplete = true;
      try {
        this.audiencesIncomplete = await audiencesAPI.fetchAudiencesIncomplete();

        if (this.audiencesIncomplete?.audiences.length > 0) {
          this.tableIncompleteRows = this.audiencesIncomplete.audiences.map((row) => {
            return {
              ...row,
              state_desc: row.state === 'failed' ? 'the audience failed to build, please re-try or contact support' : row.state,
            };
          });
        }
      } catch (error) {
        this.error.message = "error, please refresh or contact support";
      } finally {
        this.loadingAudiencesIncomplete = false;
      }
    },
    async fetchAudiences() {
      this.loadingAudiences = true;
      try {
        this.audiences = await audiencesAPI.fetchAudiences({
          current_page: this.page + 1,
          ordering_column: this.sortColumn,
          ordering_direction: this.sortDirection,
        });

        if (this.audiences?.audiences.length > 0) {
          this.tableRows = this.audiences.audiences.map((row) => {
            return {
              ...row,
              total_members: shortenNumber(row.total_members, false),
              date_created: this.formatTime(row.date_created),
              total_spend: shortenNumber(row.total_spend, true),
              total_visits: shortenNumber(row.total_visits, false),
              avg_spend: shortenNumber(row.avg_spend, true),
              avg_ticket: shortenNumber(row.avg_ticket, true),
              msgs_sent: shortenNumber(row.msgs_sent, false),
            };
          });
        }
      } catch (error) {
        this.error.message = "error, please refresh or contact support";
      } finally {
        this.loadingAudiences = false;
      }
    },
    formatTime(time) {
      return dayjs
        .utc(time)
        .tz(this.merchantTimeZone)
        .format('MM/DD/YYYY')
    },
    createNewAudience() {
      this.$router.push(`/audiences/new`)
    }
  }
};
</script>
